import { useContext } from 'react'
import { DateTime } from 'luxon'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import { mapOrderItemTaskToModel } from '@functions/orderItemTask'
import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import { generateUrls as generateOrderUrls } from '@hooks/useOrder'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as orderItemTaskActions from '@redux/modules/orderItemTask'
import type { AppDispatch } from '@redux/store'
import type { ModuleState } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/orderItemTask'
import type {
  AgencyModel,
  CampaignModel,
  OrderItemModel,
  OrderItemTaskModel,
  OrderItemTaskRequestOptions,
  OrderItemTaskTypeModel,
  OrderModel,
  ProviderModel,
  StatusModel,
} from '@models/types'

import PageContext from '@contexts/pageContext'

const today = DateTime.now()

export const generateUrls = () => ({
  completeOrderItemTasksUrl: '#/systemManager/tasks?status_key=complete',
  currentOrderItemTasksUrl: '#/systemManager/tasks?status_key=current',
  orderItemTasksIndexUrl: '#/systemManager/tasks',
})

type CreateOrderItemTaskParams = {
  dispatch: AppDispatch,
  orderItemTaskParams: Partial<OrderItemTaskModel>,
  requestOptions?: OrderItemTaskRequestOptions,
}

const createOrderItemTask = (params: CreateOrderItemTaskParams) => {
  const { dispatch, orderItemTaskParams, requestOptions } = params
  const { createOrderItemTask: createFn } = orderItemTaskActions

  const updatedParams = mergeAndStringifyEntityObjects({}, orderItemTaskParams, ['data'])

  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    // @ts-expect-error TS2339
    const { data, success } = response
    const { campaign_id } = data?.order_item || {}

    if (success && campaign_id){
      const payload = encodeURIComponent(JSON.stringify({ orderItemTask: { id: data.id } }))
      const { approvedCampaignOrdersUrl } = generateOrderUrls({ campaign_id })

      return {
        ...response,
        redirectUrl: `${approvedCampaignOrdersUrl}?showModal=CreateOrEditOrderItemTaskModal&showModalPayload=${payload}`,
      }
    }

    return response
  })
}

export type UpdateSortOrderParams = {
  sortedIds: number[],
  dispatch: AppDispatch,
}

type UpdateOrderItemTaskParams = {
  dispatch: AppDispatch,
  orderItemTask: OrderItemTaskModel,
  orderItemTaskParams: Partial<OrderItemTaskModel>,
  requestOptions?: OrderItemTaskRequestOptions,
}

const updateOrderItemTask = (params: UpdateOrderItemTaskParams) => {
  const {
    dispatch, orderItemTask, orderItemTaskParams, requestOptions,
  } = params
  const { updateOrderItemTask: updateFn } = orderItemTaskActions

  const updatedParams = mergeAndStringifyEntityObjects(orderItemTask, orderItemTaskParams, ['data'])

  return dispatch(updateFn(updatedParams, requestOptions))
}

const completeOrderItemTask = (params: { orderItemTask: Partial<OrderItemTaskModel>, dispatch: AppDispatch }) => {
  const { orderItemTask, dispatch } = params
  const { completeOrderItemTask: completeFn } = orderItemTaskActions

  return dispatch(completeFn(orderItemTask))
}

const updateSortOrder = (params: UpdateSortOrderParams) => {
  const { sortedIds, dispatch } = params

  const { updateSortOrder: updateSortOrderFn } = orderItemTaskActions

  return dispatch(updateSortOrderFn(sortedIds))
}

const deleteOrderItemTask = (params: { orderItemTask: Partial<OrderItemTaskModel>, dispatch: AppDispatch }) => {
  const { orderItemTask, dispatch } = params
  const { deleteOrderItemTask: deleteFn } = orderItemTaskActions

  // @ts-expect-error TS2345
  return dispatch(deleteFn(orderItemTask))
}

type HydrateOrderItemTaskParams = {
  dispatch: AppDispatch,
  orderItemTask: Partial<OrderItemTaskModel>,
}

const hydrateOrderItemTask = (params: HydrateOrderItemTaskParams) => {
  const { dispatch, orderItemTask } = params
  const { hydrateOrderItemTask: hydrateFn } = orderItemTaskActions

  // @ts-expect-error TS2345
  return dispatch(hydrateFn(orderItemTask))
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: OrderItemTaskRequestOptions,
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useOrderItemTaskForm(
  orderItemTask: Partial<OrderItemTaskModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options

  const orderItemTaskForm = useForm(
    defaultFormState,
    {
      // @ts-expect-error TS2345
      entity: mapOrderItemTaskToModel(orderItemTask),
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [orderItemTask.id, orderItemTask.cache_key],
  )

  return orderItemTaskForm
}

export function useRelations(orderItemTask: Partial<OrderItemTaskModel> = {}) {
  const {
    order_item_id,
    order_item_task_type_id,
    provider_id,
    status_id,
  } = orderItemTask

  const {
    agencies, campaigns, orderItems, orderItemTaskTypes, orders, providers, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const orderItem: Partial<OrderItemModel> = order_item_id && orderItems[order_item_id]
    ? orderItems[order_item_id] : {}
  const orderItemStatus: Partial<StatusModel> = orderItem.status_id
    && statuses[orderItem.status_id] ? statuses[orderItem.status_id] : {}
  const orderItemTaskType: Partial<OrderItemTaskTypeModel> = order_item_task_type_id
    && orderItemTaskTypes[order_item_task_type_id]
    ? orderItemTaskTypes[order_item_task_type_id] : {}
  const provider: Partial<ProviderModel> = provider_id && providers[provider_id] ? providers[provider_id] : {}
  const status: Partial<StatusModel> = status_id && statuses[status_id] ? statuses[status_id] : {}

  const order: Partial<OrderModel> = orderItem.order_id && orders[orderItem.order_id]
    ? orders[orderItem.order_id] : {}
  const campaign: Partial<CampaignModel> = order.campaign_id && campaigns[order.campaign_id]
    ? campaigns[order.campaign_id] : {}
  const agency: Partial<AgencyModel> = campaign.agency_id && agencies[campaign.agency_id]
    ? agencies[campaign.agency_id] : {}

  return {
    agency,
    campaign,
    order,
    orderItem,
    orderItemStatus,
    orderItemTaskType,
    provider,
    status,
  }
}

function useOrderItemTask(initEntity: Partial<OrderItemTaskModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: orderItemTask }: { entity: OrderItemTaskModel } = useLatestEntity(initEntity, 'orderItemTasks')
  const { due_date, id } = orderItemTask

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.orderItemTasks)

  const {
    agency,
    campaign,
    order,
    orderItem,
    orderItemStatus,
    orderItemTaskType,
    provider,
    status,
  } = useRelations(orderItemTask)

  useReduxAction(
    'orderItemTasks',
    'loadOrderItemTask',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const isPending = status.key === 'pending'

  const dueDateObj = due_date ? DateTime.fromFormat(due_date, 'dd/MM/yyyy') : undefined
  const isPastDueDate = dueDateObj && today > dueDateObj
  const withinTwoDaysOfDueDate = dueDateObj && today > (dueDateObj.minus({ days: 2 }))

  const providerPermitTaskEditing = digObject(provider, 'options.permit_task_editing', false)

  const canCreateTask = orderItemStatus.key !== 'pending'
  const canEditTask = orderItemStatus.key === 'pending' || (!withinTwoDaysOfDueDate && providerPermitTaskEditing)
  const canDeleteTask = canEditTask && orderItemTaskType.title !== 'Install'

  const campaignAddress = campaign.address ? `${campaign.address}, ${campaign.suburb_name}` : ''
  const taskPrice = digObject(agency, `options.task_prices.${orderItem.provider_id}.${orderItemTaskType.short_name}`, 0)

  const taskColor = digObject(orderItemTaskType, 'options.color', '000000')

  return {
    agency,
    callbacks: {
      createOrderItemTask: (
        orderItemTaskParams: Partial<OrderItemTaskModel>,
        entityOptions?: OrderItemTaskRequestOptions,
      ) => createOrderItemTask({ orderItemTaskParams, dispatch, requestOptions: entityOptions }),
      createOrEditOrderItemTask: (customPayload?: { campaign: Partial<CampaignModel> }) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditOrderItemTaskModal',
        payload: { ...customPayload, orderItemTask },
      }),
      completeOrderItemTask: () => completeOrderItemTask({ orderItemTask, dispatch }),
      deleteOrderItemTask: () => deleteOrderItemTask({ orderItemTask, dispatch }),
      hydrateOrderItemTask: (orderItemTaskParams: Partial<OrderItemTaskModel>) => hydrateOrderItemTask({
        dispatch,
        orderItemTask: orderItemTaskParams,
      }),
      updateOrderItemTask: (
        orderItemTaskParams: Partial<OrderItemTaskModel>,
        entityOptions?: OrderItemTaskRequestOptions,
      ) => updateOrderItemTask({
        orderItemTask, orderItemTaskParams, dispatch, requestOptions: entityOptions,
      }),
      updateSortOrder: (sortedIds: number[]) => updateSortOrder({ sortedIds, dispatch }),
    },
    campaign,
    campaignAddress,
    canCreateTask,
    canEditTask,
    canDeleteTask,
    creating,
    deleting,
    isPastDueDate,
    isPending,
    loading: loading && !loadedIds.includes(orderItemTask.id),
    order,
    orderItem,
    orderItemTask,
    orderItemTaskType,
    provider,
    status,
    taskColor,
    taskPrice,
    updating,
    urls: generateUrls(),
  }
}

export default useOrderItemTask
